// utils/excelExport.js
import * as XLSX from 'xlsx';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR');
};

// Helper function to format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('fr-FR');
};

export const generatePatientExcelReport = (patientData, consultations, vitals, treatments, medicalHistory) => {
  // Créer un nouveau classeur
  const workbook = XLSX.utils.book_new();

  // 1. Feuille d'informations patient
  const patientInfo = [
    ['AFDT-SUIVIMED'],
    ['Plateforme Médicale'],
    [''],
    ['INFORMATIONS PATIENT'],
    [''],
    ['Nom', patientData.lastName || 'N/A'],
    ['Prénom', patientData.firstName || 'N/A'],
    ['Date de naissance', formatDate(patientData.dateOfBirth)],
    ['Âge', patientData.dateOfBirth ? Math.floor((new Date() - new Date(patientData.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) + ' ans' : 'N/A'],
    ['Sexe', patientData.gender || 'N/A'],
    ['Email', patientData.email || 'N/A'],
    ['Téléphone', patientData.phone || 'N/A'],
    ['Adresse', patientData.address || 'N/A'],
    ['Contact d\'urgence', patientData.emergencyContact || 'N/A'],
    ['Téléphone d\'urgence', patientData.emergencyPhone || 'N/A'],
    ['Numéro de dossier', patientData.fileNumber || 'N/A'],
    [''],
    ['Généré le', formatDateTime(new Date().toISOString())]
  ];

  const patientSheet = XLSX.utils.aoa_to_sheet(patientInfo);
  XLSX.utils.book_append_sheet(workbook, patientSheet, 'Informations Patient');

  // 2. Feuille des consultations
  if (consultations && consultations.length > 0) {
    const consultationsData = [
      ['Date', 'Motif', 'Diagnostic', 'Examens complémentaires', 'Recommandations', 'Orientation', 'Prochain RDV', 'Notes', 'Créé par', 'Créé le']
    ];

    consultations
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach(consultation => {
        consultationsData.push([
          formatDateTime(consultation.consultation_date),
          consultation.reason || 'N/A',
          consultation.diagnosis || 'N/A',
          consultation.additional_exams || 'N/A',
          consultation.recommendations || 'N/A',
          consultation.orientation || 'N/A',
          formatDateTime(consultation.next_appointment),
          consultation.notes || 'N/A',
          consultation.createdBy || 'N/A',
          formatDateTime(consultation.createdAt)
        ]);
      });

    const consultationsSheet = XLSX.utils.aoa_to_sheet(consultationsData);
    XLSX.utils.book_append_sheet(workbook, consultationsSheet, 'Consultations');
  }

  // 3. Feuille des constantes vitales
  if (vitals && vitals.length > 0) {
    const vitalsData = [
      ['Date', 'Tension systolique', 'Tension diastolique', 'Fréquence cardiaque', 'Température', 'Poids', 'Taille', 'IMC', 'Notes', 'Créé par', 'Créé le']
    ];

    vitals
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach(vital => {
        const imc = vital.weight && vital.height ? 
          (vital.weight / Math.pow(vital.height / 100, 2)).toFixed(1) : 'N/A';
        
        vitalsData.push([
          formatDateTime(vital.measurement_date),
          vital.systolic_pressure || 'N/A',
          vital.diastolic_pressure || 'N/A',
          vital.heart_rate || 'N/A',
          vital.temperature || 'N/A',
          vital.weight || 'N/A',
          vital.height || 'N/A',
          imc,
          vital.notes || 'N/A',
          vital.createdBy || 'N/A',
          formatDateTime(vital.createdAt)
        ]);
      });

    const vitalsSheet = XLSX.utils.aoa_to_sheet(vitalsData);
    XLSX.utils.book_append_sheet(workbook, vitalsSheet, 'Constantes Vitales');
  }

  // 4. Feuille des traitements
  if (treatments && treatments.length > 0) {
    const treatmentsData = [
      ['Médicament', 'Dosage', 'Fréquence', 'Date de début', 'Date de fin', 'Statut', 'Notes', 'Créé par', 'Créé le']
    ];

    treatments
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach(treatment => {
        const status = treatment.end_date ? 'Terminé' : 'Actif';
        
        treatmentsData.push([
          treatment.medication_name || 'N/A',
          treatment.dosage || 'N/A',
          treatment.frequency || 'N/A',
          formatDate(treatment.start_date),
          formatDate(treatment.end_date),
          status,
          treatment.notes || 'N/A',
          treatment.createdBy || 'N/A',
          formatDateTime(treatment.createdAt)
        ]);
      });

    const treatmentsSheet = XLSX.utils.aoa_to_sheet(treatmentsData);
    XLSX.utils.book_append_sheet(workbook, treatmentsSheet, 'Traitements');
  }

  // 5. Feuille des antécédents médicaux
  if (medicalHistory && medicalHistory.length > 0) {
    const historyData = [
      ['Type', 'Titre', 'Date de début', 'Date de fin', 'Sévérité', 'Statut', 'Description', 'Créé par', 'Créé le']
    ];

    medicalHistory
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach(history => {
        historyData.push([
          history.history_type || 'N/A',
          history.title || 'N/A',
          formatDate(history.start_date),
          formatDate(history.end_date),
          history.severity || 'N/A',
          history.status || 'N/A',
          history.description || 'N/A',
          history.createdBy || 'N/A',
          formatDateTime(history.createdAt)
        ]);
      });

    const historySheet = XLSX.utils.aoa_to_sheet(historyData);
    XLSX.utils.book_append_sheet(workbook, historySheet, 'Antécédents');
  }

  // 6. Feuille de résumé
  const summaryData = [
    ['RÉSUMÉ DU DOSSIER MÉDICAL'],
    [''],
    ['Patient', `${patientData.firstName || ''} ${patientData.lastName || ''}`],
    ['Date de génération', formatDateTime(new Date().toISOString())],
    [''],
    ['STATISTIQUES'],
    ['Nombre de consultations', consultations ? consultations.length : 0],
    ['Nombre de mesures vitales', vitals ? vitals.length : 0],
    ['Nombre de traitements', treatments ? treatments.length : 0],
    ['Nombre d\'antécédents', medicalHistory ? medicalHistory.length : 0],
    [''],
    ['Traitements actifs', treatments ? treatments.filter(t => !t.end_date).length : 0],
    ['Traitements terminés', treatments ? treatments.filter(t => t.end_date).length : 0]
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Résumé');

  // Générer le fichier Excel
  const fileName = `dossier-${patientData.lastName || 'patient'}-${patientData.firstName || ''}-${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};
